import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";

const ManageProducts = () => {
  const { getProducts, patchProduct } = useApi();
  const { stripeAccountId, isCoach } = useUserInfo();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [metaEdits, setMetaEdits] = useState({});

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await getProducts(stripeAccountId);
        setProducts(data || []);
        const initialMeta = {};
        (data || []).forEach((product) => {
          product.prices.forEach((price) => {
            initialMeta[price.priceId] = {
              description: price.metadata?.description || "",
              quantity: price.metadata?.quantity || "",
            };
          });
        });
        setMetaEdits(initialMeta);
      } catch (err) {
        console.error("Failed to load products:", err);
        setError("Unable to load products. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [getProducts, stripeAccountId]);

  const handleMetaChange = (priceId, key, value) => {
    setMetaEdits((prev) => ({
      ...prev,
      [priceId]: {
        ...prev[priceId],
        [key]: value,
      },
    }));
  };

  const handleMetaBlur = async (priceId, isRecurring) => {
    const meta = metaEdits[priceId] || {};
    try {
      await patchProduct({
        priceId,
        description: meta.description,
        quantity: isRecurring ? undefined : meta.quantity,
        stripeAccountId,
      });
    } catch (err) {
      console.error("Failed to update product metadata:", err);
      setError("Failed to update product metadata. Please try again.");
    }
  };

  const content = useMemo(() => {
    if (!isCoach) {
      return (
        <Alert severity="warning">
          This page is for coaches. Please sign in as a coach to manage
          products.
        </Alert>
      );
    }

    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!products.length) {
      return (
        <Alert severity="info">
          No products found. Create products in your Stripe Dashboard to see
          them here.
        </Alert>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {products.map((product) => (
          <Card key={product.productId}>
            <CardContent>
              <Typography variant="h6">{product.name}</Typography>
              {product.metadata?.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  {product.metadata.description}
                </Typography>
              )}
              <Divider sx={{ mb: 1.5 }} />
              {product.prices.map((price) => {
                const meta = metaEdits[price.priceId] || {
                  description: "",
                  quantity: "",
                };
                const isRecurring = !!price.recurring;
                return (
                  <Box
                    key={price.priceId}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle1">
                        ${(price.unitAmount / 100).toFixed(2)}{" "}
                        {price.currency?.toUpperCase()}
                      </Typography>
                      {isRecurring && (
                        <Chip label="Recurring" size="small" color="primary" />
                      )}
                    </Box>
                    <TextField
                      label="Description"
                      value={meta.description}
                      onChange={(e) =>
                        handleMetaChange(
                          price.priceId,
                          "description",
                          e.target.value
                        )
                      }
                      onBlur={() => handleMetaBlur(price.priceId, isRecurring)}
                      fullWidth
                      multiline
                      minRows={2}
                      sx={{ mb: !isRecurring ? 1 : 0 }}
                    />
                    {!isRecurring && (
                      <TextField
                        label="Quantity"
                        type="number"
                        value={meta.quantity}
                        onChange={(e) =>
                          handleMetaChange(
                            price.priceId,
                            "quantity",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          handleMetaBlur(price.priceId, isRecurring)
                        }
                        fullWidth
                      />
                    )}
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }, [isCoach, loading, metaEdits, products, stripeAccountId]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Manage Products
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        View your Stripe products and edit descriptions or quantities for
        non-recurring products. For adding or editing products further, visit
        the Products page in your{" "}
        <a
          href={`https://dashboard.stripe.com/${stripeAccountId || ""}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Stripe Dashboard
        </a>
        .
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {content}
    </Container>
  );
};

export default ManageProducts;
